import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { CATEGORIES } from "@/lib/constants";

export default function Catalogo() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("tutti");
  const [sort, setSort] = useState("recenti");
  const [query, setQuery] = useState("");

  useEffect(() => {
    client
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== "tutti") list = list.filter((p) => p.category === category);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    else list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [products, category, sort, query]);

  const counts = useMemo(() => {
    const c = { tutti: products.length };
    for (const cat of CATEGORIES) c[cat.slug] = products.filter((p) => p.category === cat.slug).length;
    return c;
  }, [products]);

  return (
    <main className="mx-auto max-w-7xl px-5 pb-28 pt-32 md:px-8 md:pt-40" data-testid="catalogo-page">
      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">La vetrina</p>
      </Reveal>
      <MaskedLine className="mt-4 font-serif text-5xl md:text-7xl" delay={0.1}>Catalogo</MaskedLine>
      <Reveal delay={0.25}>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg">
          Gioielli in oro e orologi di prestigio, selezionati e garantiti. Ogni pezzo è unico: contattaci per disponibilità e dettagli.
        </p>
      </Reveal>

      <div className="sticky top-16 z-30 mt-12 border-y border-white/10 bg-[#050505]/90 py-4 backdrop-blur-xl md:top-20" data-testid="catalog-filters">
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="filter-tutti"
            onClick={() => setCategory("tutti")}
            className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              category === "tutti" ? "border-gold bg-gold text-black" : "border-white/15 text-[#A3A3A3] hover:border-gold/60 hover:text-gold"
            }`}
          >
            Tutti · {counts.tutti}
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => setCategory(c.slug)}
              className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
                category === c.slug ? "border-gold bg-gold text-black" : "border-white/15 text-[#A3A3A3] hover:border-gold/60 hover:text-gold"
              }`}
            >
              {c.label} · {counts[c.slug]}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
              <input
                data-testid="catalog-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca..."
                className="w-40 border border-white/15 bg-transparent py-2 pl-9 pr-3 text-sm text-[#FAFAFA] placeholder:text-[#737373]"
              />
            </div>
            <select
              data-testid="catalog-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-white/15 bg-[#050505] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]"
            >
              <option value="recenti">Più recenti</option>
              <option value="price_asc">Prezzo crescente</option>
              <option value="price_desc">Prezzo decrescente</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="py-24 text-center text-sm uppercase tracking-[0.3em] text-[#737373]" data-testid="catalog-loading">Caricamento…</p>
      ) : filtered.length === 0 ? (
        <div className="py-24 text-center" data-testid="catalog-empty">
          <p className="font-serif text-2xl italic text-[#A3A3A3]">Nessun gioiello trovato.</p>
          <p className="mt-3 text-sm text-[#737373]">Prova a cambiare filtro o scrivici: in negozio trovi molto di più.</p>
        </div>
      ) : (
        <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4" data-testid="catalog-grid">
          {filtered.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </main>
  );
}
